import { createRef, Component } from "react";
import {
  arrayOf,
  bool,
  func,
  number,
  oneOfType,
  shape,
  string,
} from "prop-types";

import { URL_API_LISTINGS_COMPARE } from "../../constants";
import {
  api,
  getApiErrorMessage,
  scrollIfNeeded,
  validateInput,
} from "../../helpers";
import { colors } from "../../styles/Styles.variables";

import Alert from "../../styles/components/Alert";
import Button from "../../styles/components/Button";
import Checkbox from "../../styles/components/Checkbox";
import EmailOptIn from "../email-opt-in";
import Form from "../form";
import Group from "../form/components/Group";
import Label from "../form/components/Label";
import Modal from "../modal";
import Recaptcha from "../recaptcha";
import { RecaptchaDisclaimer } from "../recaptcha/components";
import Row from "../form/components/Row";
import Text from "../form/components/Text";
import TextArea from "../form/components/TextArea";

class ModalPdfs extends Component {
  constructor(props) {
    super(props);

    this.getRecaptchaToken = this.getRecaptchaToken.bind(this);
    this.handleApiError = this.handleApiError.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handleSendCopy = this.handleSendCopy.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.postData = this.postData.bind(this);

    this.form = createRef();
    this.header = createRef();

    this.state = {
      email: "",
      emailError: null,
      inProgress: false,
      first_name: "",
      first_nameError: null,
      last_name: "",
      last_nameError: null,
      recipients: "",
      recipientsError: null,
      sendCopy: false,
      serverError: null,
      success: false,
    };
  }

  /**
   * Get reCaptcha token
   * @public
   */
  getRecaptchaToken() {
    const { handleApiError } = this;
    const { recaptchaLoaded, requestRecaptchaToken } = this.props;

    if (!recaptchaLoaded || !requestRecaptchaToken) {
      this.handleApiError("reCAPTCHA did not load");
      return false;
    }

    requestRecaptchaToken("contact")
      .then(token => {
        if (!token) {
          handleApiError("reCAPTCHA failed to execute");
          return false;
        }

        this.postData(token);
      })
      .catch(error => handleApiError(error));
  }

  /**
   * Handle API error
   * @param {*}
   * @public
   */
  handleApiError(error) {
    const message = getApiErrorMessage(error);

    this.setState({ inProgress: false, serverError: message }, () =>
      scrollIfNeeded(this.header.current)
    );
  }

  /**
   * Handle input change
   * @param {event|object} e
   * @public
   */
  handleInput(e) {
    const { target: input } = e;
    const field = input.id;
    const value = !!input ? input.value : null;
    let error = validateInput(input);

    if (
      field === "recipients" &&
      !!value &&
      !/^[^\s@,]+@[^\s@,]+\.[^\s@,]+(, ?[^\s@,]+@[^\s@,]+\.[^\s@,]+)*$/.test(
        value
      )
    ) {
      error = "Enter a comma-separated list of valid email addresses";
    }

    if (!!field) {
      this.setState({ [field]: value, [`${field}Error`]: error });
    }
  }

  /**
   * Handle send copy checkbox
   * @public
   */
  handleSendCopy() {
    this.setState(({ sendCopy }) => ({ sendCopy: !sendCopy }));
  }

  /**
   * Handle form submission
   * @param {event} e
   * @public
   */
  handleSubmit(e) {
    e.preventDefault();

    const { current: form } = this.form;

    if (!form) {
      return false;
    }

    if (!form.checkValidity() || !!this.state.recipientsError) {
      [...form.elements].forEach(input => {
        this.handleInput({ target: input });
      });
    } else {
      this.getRecaptchaToken();
    }
  }

  /**
   * Post data to API
   * @public
   */
  postData(recaptchaToken) {
    this.setState({ inProgress: true });

    const { handleApiError } = this;
    const { authenticated, emailOptIn, listings } = this.props;
    const { email, first_name, last_name, recipients, sendCopy } = this.state;
    const data = {
      email: email,
      emailOptIn: emailOptIn,
      emails: recipients.replace(/ /g, "").split(","),
      listings: listings.map(listing => listing.equipmentId),
      first_name: first_name,
      last_name: last_name,
      name: `${first_name} ${last_name}`,
      pluralized: listings.length > 1 ? "s" : "",
      recaptchaToken: recaptchaToken,
      sendCopy: sendCopy,
    };

    api
      .post(URL_API_LISTINGS_COMPARE, data)
      .then(response => {
        if (!!response.data && !!response.data.success) {
          if (!!this.props.trackEvent) {
            this.props.trackEvent({
              category: "Form",
              action: "Submit",
              label: "Email PDFs Modal",
            });
          }

          this.setState(
            { inProgress: false, serverError: null, success: true },
            () => {
              if (authenticated) {
                this.props.updateUser({
                  first_name: data.first_name,
                  last_name: data.last_name,
                  email: data.email,
                });
              }
              scrollIfNeeded(this.header.current);
            }
          );
        } else {
          handleApiError(response);
        }
      })
      .catch(handleApiError);
  }

  componentDidUpdate(prevProps, prevState) {
    if (!this.props.open && prevProps.open) {
      this.setState({
        serverError: null,
        success: false,
      });
    }

    if (!prevProps.open && this.props.open && !!this.props.trackModalView) {
      this.props.trackModalView("pdfs", {
        category: "click",
        action: "Email Listings",
        label: this.props.analyticsLabel,
      });
    }
  }

  render() {
    const { form, handleInput, handleSendCopy, handleSubmit, header } = this;
    const { authenticated, close, listings, open, targetDevice } = this.props;
    const {
      email,
      emailError,
      inProgress,
      first_name,
      first_nameError,
      last_name,
      last_nameError,
      recipients,
      recipientsError,
      sendCopy,
      serverError,
      success,
    } = this.state;
    const shouldStack = targetDevice === "mobile";

    return (
      <Modal aria-label="Email PDF" close={close} open={open}>
        <Recaptcha />
        <header ref={header}>
          <h1>
            <strong>Email {listings.length}</strong>
            {" "}
            Listing{listings.length > 1 ? "s" : ""}
          </h1>
          {listings.length > 1 ? (
            <p>
              Send links to the listings on this Compare page and a link to
              download PDFs of all <strong>{listings.length} listings</strong>.
            </p>
          ) : (
            <p>
              Send a link to the listing on this Compare page and a link to
              download a PDF of <strong>{listings.length} listing</strong>.
            </p>
          )}
          {serverError && <Alert type="error">{serverError}</Alert>}
          {success && (
            <Alert type="success">Thanks! We’ll be in touch soon.</Alert>
          )}
        </header>
        {!success && (
          <Form onSubmit={handleSubmit} formRef={form}>
            <div className="form-fields">
              <Row stack={shouldStack}>
                <Group error={first_nameError}>
                  <Label htmlFor="first_name" required>
                    Your First Name
                  </Label>
                  <Text
                    id="first_name"
                    maxLength="30"
                    onChange={handleInput}
                    required
                    value={first_name}
                  />
                </Group>
                <Group error={last_nameError}>
                  <Label htmlFor="last_name" required>
                    Your Last Name
                  </Label>
                  <Text
                    id="last_name"
                    maxLength="30"
                    onChange={handleInput}
                    required
                    value={last_name}
                  />
                </Group>
              </Row>
              <Group error={emailError}>
                <Label htmlFor="email" required>
                  Your Email
                </Label>
                <Text
                  id="email"
                  maxLength="200"
                  onChange={handleInput}
                  readOnly={authenticated}
                  required
                  type="email"
                  value={email}
                />
              </Group>
              <Group
                error={recipientsError}
                help="Separate multiple emails with comma"
              >
                <Label htmlFor="recipients" required>
                  Recipients
                </Label>
                <TextArea
                  height="short"
                  id="recipients"
                  onChange={handleInput}
                  required
                  value={recipients}
                />
              </Group>
              <Group labelOffset="small">
                <Checkbox
                  checked={sendCopy}
                  color={colors.white}
                  id="send-copy"
                  onChange={handleSendCopy}
                >
                  Send me a copy of this email
                </Checkbox>
              </Group>
              <EmailOptIn />
            </div>
            <RecaptchaDisclaimer className="form-disclaimer" />
            <div className="form-actions">
              <Button
                block={targetDevice === "mobile"}
                color="green400"
                disabled={inProgress}
                size="small"
                square={targetDevice === "mobile"}
                type="submit"
              >
                {inProgress ? "Sharing…" : "Share"}
              </Button>
            </div>
          </Form>
        )}
      </Modal>
    );
  }
}

ModalPdfs.propTypes = {
  analyticsLabel: string,
  authenticated: bool,
  close: func.isRequired,
  emailOptIn: bool,
  listings: arrayOf(
    shape({
      equipmentId: oneOfType([number, string]),
    })
  ).isRequired,
  open: bool,
  recaptchaLoaded: bool,
  requestRecaptchaToken: func.isRequired,
  targetDevice: string,
  trackEvent: func,
  trackModalView: func,
  updateUser: func.isRequired,
  user: shape({
    email: string,
    name: string,
  }),
};

ModalPdfs.defaultProps = {
  analyticsLabel: "modal",
  listings: [],
  open: false,
};

ModalPdfs.getDerivedStateFromProps = (nextProps, prevState) => {
  const { user } = nextProps;
  const { email, first_name, last_name } = prevState;
  const nextState = {};

  if (!!user && !!user.email && !email) {
    nextState.email = user.email;
  }

  if (!!user && !!user.first_name && !first_name) {
    nextState.first_name = user.first_name;
  }

  if (!!user && !!user.last_name && !last_name) {
    nextState.last_name = user.last_name;
  }

  return !!Object.keys(nextState).length ? nextState : null;
};

export default ModalPdfs;
